import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/Table'
import { toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import type { IKeyword } from 'entities/Keywords'
import { KeywordsApi } from 'entities/Keywords/api/keywords'
import type {
  IParamsGetKeywords,
  IResponseGetKeywords,
  IResponseKeyword,
} from 'entities/Keywords/api/type'
import { Keyword } from 'entities/Keywords/model/Keyword'
import { UsersApi } from 'entities/Users'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import { FiltersAndSearchStore } from 'widgets/FiltersAndSearch'

const KEYWORDS_COLUMNS_SOURCE_TYPE = 'keyword-table-columns'

export type ISetIsKeywordsActionShown = (isShown: boolean) => void

export class KeywordsListStore {
  private _page = 1
  private _total = 0
  private _limit = tableLimitStore.getLimit('keywords-table') ?? 10
  private _hasRetriedAtMinLimit = false
  private _isInitialLoading = true
  private _keywordsMap = new Map<number, IKeyword>()
  private _tableStore: TableStore<IKeyword>
  private _disposeLoadKeywords: IReactionDisposer | null = null
  private _disposeKeywordsActionShown: IReactionDisposer | null = null
  private _disposeRequestParams: IReactionDisposer | null = null
  private readonly _filtersAndSearchStore: FiltersAndSearchStore

  constructor(
    setIsKeywordsActionShown: ISetIsKeywordsActionShown,
    private _entities: IEntitiesStore
  ) {
    this._tableStore = new TableStore<IKeyword>({
      element: 'keyword',
      sortBy: 'created_at',
      sortOrder: 'desc',
      saveColumns: this._saveColumns,
      getColumns: this._getColumns(),
    })

    this._filtersAndSearchStore = new FiltersAndSearchStore(
      {
        getFilters: () => KeywordsApi.getFilters().then(({ data }) => data),
      },
      this._entities
    )

    makeAutoObservable(this)

    this.reactionKeywordsActionShown(setIsKeywordsActionShown)
    this.reactionLoadKeywords()
  }

  private _saveColumns = async (items: string[]) => {
    try {
      await UsersApi.updateUsersUiSettings({
        source_type: KEYWORDS_COLUMNS_SOURCE_TYPE,
        items,
      })
    } catch (e) {
      logger.error(e)
    }
  }

  private _getColumns = async (): Promise<string[] | undefined> => {
    try {
      const { data } = await UsersApi.getUsersUiSettings(KEYWORDS_COLUMNS_SOURCE_TYPE)
      const items = data.data?.items
      if (!items?.length) return undefined
      return items
    } catch (e) {
      logger.error(e)
      return undefined
    }
  }

  get requestParams(): IParamsGetKeywords {
    // TODO: move to BE
    const filterList = this._filtersAndSearchStore.params.filtersList.map((item) => ({
      ...item,
      filters: item.filters.map((filter) => {
        if (filter.key === 'keywords.is_active') {
          const mapper = (value: string) => (value === 'active' ? '1' : '0')

          if (Array.isArray(filter.value)) {
            const newFilterValue = filter.value.map(mapper)

            return { ...filter, value: newFilterValue }
          }
        }
        return filter
      }),
    }))

    return {
      limit: this.limit || undefined,
      sortBy: this.tableStore.sortBy,
      sortOrder: this.tableStore.sortOrder,
      term: this._filtersAndSearchStore.params.search,
      filtersList: filterList,
    }
  }

  get paramsGetItems(): IParamsGetKeywords {
    return {
      page: this._page,
      ...this.requestParams,
    }
  }

  get limit() {
    return this._limit
  }

  get isLoading() {
    return this._requestLoadKeywords.loading
  }

  get isInitialLoading() {
    return this._isInitialLoading
  }

  get page() {
    return this._page
  }

  get tableStore() {
    return this._tableStore
  }

  get total() {
    return this._total
  }

  get isKeywordsEmpty() {
    return (
      !this.isLoading && !this._keywordsMap.size && !this._filtersAndSearchStore.hasSearchParams
    )
  }

  get isNoSearchResults() {
    return !this.isLoading && !this._keywordsMap.size && this._filtersAndSearchStore.hasSearchParams
  }

  get keywords() {
    return Array.from(this._keywordsMap.values())
  }

  get filtersAndSearchStore() {
    return this._filtersAndSearchStore
  }

  reactionLoadKeywords = () => {
    this._disposeLoadKeywords?.()
    this._disposeRequestParams?.()

    this._disposeRequestParams = reaction(
      () => this.requestParams,
      () => {
        this._requestLoadKeywords.setLoading(true)
        this._page = 1
      }
    )

    this._disposeLoadKeywords = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadKeywords.debounceLoadData()
    )
  }

  private _requestLoadKeywords = new RequestLoadData<boolean>({
    preRequestCallback: (noLoading) => {
      if (noLoading) {
        this._requestLoadKeywords.setLoading(false)
      }
    },
    requestCallback: async ({ cancelRequest }) => {
      try {
        const { data } = await KeywordsApi.getKeywords(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })

        runInAction(() => {
          this._hasRetriedAtMinLimit = false
        })
        this.setGetKeywordsResponse(data)
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: keywords-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this._limit
          const newLimit = tableLimitStore.reduceLimit('keywords-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
            })
            this._limit = newLimit
            tableLimitStore.setLimit('keywords-table', newLimit)
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
            })
            this._requestLoadKeywords.debounceLoadData()
          }
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        this._isInitialLoading = false
      })
    },
  })

  loadKeywords = async (noLoading?: boolean) => {
    await this._requestLoadKeywords.loadData(noLoading)
  }

  setGetKeywordsResponse = ({ data, meta }: IResponseGetKeywords) => {
    this._keywordsMap.clear()
    this._tableStore.setRows([])

    this.setKeywordMap(data)
    this._tableStore.setRows(this.keywords)
    this._page = meta.current_page
    this._limit = meta.per_page
    this._total = meta.total
  }

  setKeywordMap = (keywordsResponse: IResponseKeyword[]) => {
    keywordsResponse.forEach((keywordResponse) => {
      try {
        this._keywordsMap.set(keywordResponse.id, new Keyword(keywordResponse, this._entities))
      } catch (e) {
        logger.error(e)
      }
    })
  }

  reactionKeywordsActionShown = (setIsKeywordsActionShown: ISetIsKeywordsActionShown) => {
    this._disposeKeywordsActionShown?.()
    this._disposeKeywordsActionShown = reaction(
      () => [this._requestLoadKeywords.loading, this._keywordsMap.size],
      () => setIsKeywordsActionShown(!this.isKeywordsEmpty)
    )
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
    tableLimitStore.setLimit('keywords-table', limit)
  }

  dispose = () => {
    this.clearReactions()
    this._requestLoadKeywords.dispose()
  }

  clearReactions = () => {
    this._disposeKeywordsActionShown?.()
    this._disposeLoadKeywords?.()
    this._disposeRequestParams?.()
  }

  onDelete = (id: number) => {
    let noLoading = true
    this._keywordsMap.delete(id)

    if (this._keywordsMap.size === 0 && this.page > 1) {
      this._page -= 1
      noLoading = false
    }
    void this.loadKeywords(noLoading)
  }

  onBulkDelete = () => {
    const bulkAll = this._tableStore.bulkAllMode
    const ids = this._tableStore.selectedIds as number[]
    const idsLength = ids.length
    let noLoading = true
    const modalId = nanoid()

    const handleDelete = async () => {
      this._requestLoadKeywords.setLoading(true)
      modalStore.closeModal(modalId)

      try {
        await KeywordsApi.deleteBulkKeywords(ids, bulkAll)
        toastStore.add({
          title: bulkAll ? 'All keywords deleted' : `Keyword${idsLength > 1 ? 's' : ''} deleted`,
          type: 'success',
        })

        this._tableStore.resetSelected()
        this._keywordsMap.clear()
        if (this._keywordsMap.size === 0 && this.page > 1) {
          this._page -= 1
          noLoading = false
        }
        this.loadKeywords(noLoading)
      } catch (error) {
        runInAction(() => {
          this._requestLoadKeywords.setLoading(false)
          toastStore.add({
            title: 'Something went wrong. Please try again.',
            type: 'error',
          })
        })
      }
    }

    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.ALERT,
      title: bulkAll
        ? 'Delete all keywords?'
        : `Delete ${idsLength} keyword${idsLength > 1 ? 's' : ''}?`,
      desc: 'This action cannot be undone',
      primaryAction: {
        text: 'Delete',
        onAction: handleDelete,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => modalStore.closeModal(modalId),
      },
    })
  }
}
